import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Mail, CalendarClock, Zap, Pencil, Play, Eye, Send, ListChecks, RefreshCw, Plus, Trash2,
  Smartphone, Monitor,
} from "lucide-react";

const JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const AUDIENCES = [
  { value: "user_self", label: "L'utilisateur concerné" },
  { value: "affected_student", label: "L'étudiant concerné par l'événement" },
  { value: "all_active_students", label: "Tous les étudiants actifs" },
  { value: "students_with_shifts_next_week", label: "Étudiants ayant un shift la semaine suivante" },
  { value: "students_without_availabilities_next_month", label: "Étudiants sans dispos pour le mois suivant" },
  { value: "all_admins", label: "Admins et chefs d'atelier" },
];

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewViewport, setPreviewViewport] = useState("desktop");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [t, j, l] = await Promise.all([
      api.get("/email-templates"),
      api.get("/email-templates/_/jobs"),
      api.get("/email-logs", { params: { limit: 20 } }),
    ]);
    setTemplates(t.data);
    setJobs(j.data.jobs || []);
    setLogs(l.data || []);
  };

  useEffect(() => { load(); }, []);

  const openEdit = (tpl) => {
    setCreating(false);
    setEditing({
      ...tpl,
      _schedule_type: tpl.schedule?.type || (tpl.trigger_type === "schedule" ? "weekly" : null),
      _weekday: tpl.schedule?.weekday ?? 0,
      _day: tpl.schedule?.day ?? 1,
      _hour: tpl.schedule?.hour ?? 9,
      _minute: tpl.schedule?.minute ?? 0,
      _audience: tpl.audience?.target || "all_active_students",
    });
  };

  const openCreate = () => {
    setCreating(true);
    setEditing({
      key: null,
      name: "",
      description: "",
      subject: "",
      body_html: "<p>Bonjour <b>{{ name }}</b>,</p>\n<p>Votre message ici…</p>",
      body_text: "Bonjour {{ name }}, …",
      trigger_type: "schedule",
      enabled: true,
      system: false,
      variables_help: ["name", "email", "login_url", "planning_url", "dispos_url"],
      _schedule_type: "weekly",
      _weekday: 0,
      _day: 1,
      _hour: 9,
      _minute: 0,
      _audience: "all_active_students",
    });
  };

  const saveEdit = async () => {
    setBusy(true);
    const schedulePart = {
      type: editing._schedule_type,
      hour: Number(editing._hour),
      minute: Number(editing._minute),
    };
    if (editing._schedule_type === "weekly") schedulePart.weekday = Number(editing._weekday);
    else if (editing._schedule_type === "monthly") schedulePart.day = Number(editing._day);

    try {
      if (creating) {
        if (!editing.name?.trim() || !editing.subject?.trim() || !editing.body_html?.trim()) {
          toast.error("Nom, sujet et contenu HTML sont obligatoires");
          setBusy(false);
          return;
        }
        await api.post(`/email-templates`, {
          name: editing.name,
          description: editing.description,
          subject: editing.subject,
          body_html: editing.body_html,
          body_text: editing.body_text,
          schedule: schedulePart,
          audience: { target: editing._audience },
          enabled: editing.enabled,
        });
        toast.success("Nouvel email créé");
      } else {
        const updates = {
          name: editing.name,
          description: editing.description,
          subject: editing.subject,
          body_html: editing.body_html,
          body_text: editing.body_text,
          enabled: editing.enabled,
          audience: { target: editing._audience },
        };
        if (editing.trigger_type === "schedule") updates.schedule = schedulePart;
        await api.patch(`/email-templates/${editing.key}`, updates);
        toast.success("Template enregistré");
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const removeTemplate = async (tpl) => {
    if (!window.confirm(`Supprimer définitivement « ${tpl.name} » ?`)) return;
    try {
      await api.delete(`/email-templates/${tpl.key}`);
      toast.success("Email supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runNow = async (tpl) => {
    if (!window.confirm(`Déclencher l'envoi réel de « ${tpl.name} » à toute son audience maintenant ?`)) return;
    try {
      const { data } = await api.post(`/email-templates/${tpl.key}/run-now`);
      toast.success(`${data.sent} email(s) envoyé(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendTest = async (tpl) => {
    const to = window.prompt(`Email pour le test (laisse vide pour t'envoyer à toi) :`, "");
    try {
      await api.post(`/email-templates/${tpl.key}/test`, { to: to || undefined });
      toast.success("Email de test envoyé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openPreview = async (tpl) => {
    try {
      const { data } = await api.post(`/email-templates/${tpl.key}/preview`, {});
      setPreview({ ...data, name: tpl.name });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleEnabled = async (tpl, val) => {
    try {
      await api.patch(`/email-templates/${tpl.key}`, { enabled: val });
      toast.success(val ? "Activé" : "Désactivé");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const describeSchedule = (tpl) => {
    if (tpl.trigger_type === "event") return `Événement : ${tpl.event_key}`;
    const s = tpl.schedule || {};
    const time = `${String(s.hour ?? 9).padStart(2, "0")}:${String(s.minute ?? 0).padStart(2, "0")}`;
    if (s.type === "weekly") return `Chaque ${JOURS[s.weekday ?? 0].toLowerCase()} à ${time}`;
    if (s.type === "monthly") return `Le ${s.day ?? 1} du mois à ${time}`;
    if (s.type === "daily") return `Chaque jour à ${time}`;
    return "—";
  };

  return (
    <div className="space-y-6" data-testid="page-email-templates">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-tiny">Communication</p>
          <h1 className="font-display text-3xl sm:text-4xl text-[#1A2B27] mt-1">Emails automatiques</h1>
          <p className="text-[#6b7f79] mt-1">Édite le contenu, l'audience et la planification de chaque email envoyé par l'application.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={openCreate} data-testid="email-create" className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3528] shadow-md">
            <Plus className="w-4 h-4 mr-2" /> Nouvel email
          </Button>
          <Button onClick={load} variant="outline" data-testid="email-refresh" className="rounded-full border-[#DDE7E4] text-[#6b7f79]">
            <RefreshCw className="w-4 h-4 mr-2" /> Rafraîchir
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {templates.map((t) => {
          const isEvent = t.trigger_type === "event";
          return (
            <Card key={t.key} className="card-soft border-0" data-testid={`tpl-card-${t.key}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isEvent ? "bg-[#C9DBD6] text-[#3C6750]" : "bg-[#B8C7C2] text-[#1A2B27]"}`}>
                    {isEvent ? <Zap className="w-5 h-5" /> : <CalendarClock className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#1A2B27] truncate">{t.name}</div>
                    <div className="text-xs text-[#94ADA7] mt-0.5">{describeSchedule(t)}</div>
                    <p className="text-[11px] text-[#94ADA7] mt-2 leading-snug">{t.description}</p>
                    {t.last_sent_at && (
                      <p className="text-[10px] text-[#A1887F] mt-2">
                        Dernier envoi : {new Date(t.last_sent_at).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" })}
                        {t.last_sent_count != null && ` · ${t.last_sent_count} destinataire(s)`}
                      </p>
                    )}
                  </div>
                  <Switch
                    checked={!!t.enabled}
                    onCheckedChange={(v) => toggleEnabled(t, v)}
                    data-testid={`tpl-toggle-${t.key}`}
                  />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`tpl-edit-${t.key}`} className="rounded-full text-[#6b7f79]">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Éditer
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openPreview(t)} data-testid={`tpl-preview-${t.key}`} className="rounded-full text-[#6b7f79]">
                    <Eye className="w-3.5 h-3.5 mr-1" /> Aperçu
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => sendTest(t)} data-testid={`tpl-test-${t.key}`} className="rounded-full text-[#6b7f79]">
                    <Send className="w-3.5 h-3.5 mr-1" /> Test
                  </Button>
                  {t.trigger_type === "schedule" && (
                    <Button size="sm" variant="ghost" onClick={() => runNow(t)} data-testid={`tpl-runnow-${t.key}`} className="rounded-full text-[#3C6750]">
                      <Play className="w-3.5 h-3.5 mr-1" /> Envoyer
                    </Button>
                  )}
                  {!t.system && (
                    <Button size="sm" variant="ghost" onClick={() => removeTemplate(t)} data-testid={`tpl-delete-${t.key}`} className="rounded-full text-[#C62828] hover:bg-[#FFEBEE]">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="card-soft border-0">
        <CardContent className="p-5">
          <h2 className="font-display text-xl text-[#1A2B27] flex items-center gap-2 mb-3">
            <ListChecks className="w-5 h-5 text-[#2C4C3B]" /> Prochains envois planifiés
          </h2>
          {jobs.length === 0 ? (
            <p className="text-sm text-[#94ADA7]">Aucun job actif.</p>
          ) : (
            <ul className="space-y-1 text-sm text-[#1A2B27]">
              {jobs.map((j) => (
                <li key={j.id} className="flex justify-between" data-testid={`job-${j.id}`}>
                  <span className="font-mono text-xs text-[#6b7f79]">{j.id}</span>
                  <span>{j.next_run ? new Date(j.next_run).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" }) : "—"}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="card-soft border-0">
        <CardContent className="p-5">
          <h2 className="font-display text-xl text-[#1A2B27] flex items-center gap-2 mb-3">
            <Mail className="w-5 h-5 text-[#2C4C3B]" /> Historique des envois (20 derniers)
          </h2>
          {logs.length === 0 ? (
            <p className="text-sm text-[#94ADA7]">Aucun envoi encore.</p>
          ) : (
            <div className="space-y-1 text-xs">
              {logs.map((l) => (
                <div key={`${l.sent_at}-${l.to}-${l.template_key}`} className="flex flex-wrap gap-x-3 items-baseline border-b border-[#DDE7E4] py-1.5" data-testid={`log-${l.sent_at}`}>
                  <span className="text-[#A1887F] w-32 shrink-0">{new Date(l.sent_at).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" })}</span>
                  <Badge variant="outline" className="border-[#DDE7E4] text-[#6b7f79] rounded-full">{l.template_key}</Badge>
                  <span className="text-[#1A2B27] flex-1 min-w-0 truncate">→ {l.to}</span>
                  {l.ok ? <Badge className="bg-[#E8F5E9] text-[#2E7D32] hover:bg-[#E8F5E9] rounded-full">OK</Badge>
                        : <Badge variant="destructive" className="rounded-full">ÉCHEC</Badge>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog édition / création */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setCreating(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tpl-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A2B27]">
              {creating ? "Nouvel email automatique" : editing?.name}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="text-xs text-[#94ADA7] bg-[#F5FAF8] p-3 rounded-xl">
                <b>Variables disponibles :</b>{" "}
                {(editing.variables_help || []).map((v) => (
                  <code key={v} className="mr-1 bg-white px-1.5 py-0.5 rounded text-[#3C6750]">{`{{ ${v} }}`}</code>
                ))}
              </div>

              <div>
                <Label className="label-tiny">Nom (affichage interne)</Label>
                <Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 h-11 rounded-xl border-[#DDE7E4]" data-testid="tpl-edit-name" />
              </div>

              <div>
                <Label className="label-tiny">Description</Label>
                <Textarea value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} className="mt-1 rounded-xl border-[#DDE7E4]" data-testid="tpl-edit-desc" />
              </div>

              <div>
                <Label className="label-tiny">Sujet de l'email</Label>
                <Input value={editing.subject || ""} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} className="mt-1 h-11 rounded-xl border-[#DDE7E4]" data-testid="tpl-edit-subject" />
              </div>

              <div>
                <Label className="label-tiny">Contenu HTML</Label>
                <Textarea value={editing.body_html || ""} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })} rows={10} className="mt-1 rounded-xl border-[#DDE7E4] font-mono text-xs" data-testid="tpl-edit-body-html" />
              </div>

              <div>
                <Label className="label-tiny">Contenu texte (fallback)</Label>
                <Textarea value={editing.body_text || ""} onChange={(e) => setEditing({ ...editing, body_text: e.target.value })} rows={4} className="mt-1 rounded-xl border-[#DDE7E4] font-mono text-xs" data-testid="tpl-edit-body-text" />
              </div>

              <div>
                <Label className="label-tiny">Audience</Label>
                <Select value={editing._audience} onValueChange={(v) => setEditing({ ...editing, _audience: v })}>
                  <SelectTrigger className="mt-1 h-11 rounded-xl border-[#DDE7E4]" data-testid="tpl-edit-audience"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AUDIENCES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {editing.trigger_type === "schedule" && (
                <div className="bg-[#F7F9F8] p-4 rounded-xl space-y-3">
                  <p className="label-tiny">Planification</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[10px] text-[#94ADA7]">Type</Label>
                      <Select value={editing._schedule_type} onValueChange={(v) => setEditing({ ...editing, _schedule_type: v })}>
                        <SelectTrigger className="h-10 rounded-xl border-[#DDE7E4]" data-testid="tpl-edit-stype"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Hebdomadaire</SelectItem>
                          <SelectItem value="monthly">Mensuel</SelectItem>
                          <SelectItem value="daily">Quotidien</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {editing._schedule_type === "weekly" && (
                      <div>
                        <Label className="text-[10px] text-[#94ADA7]">Jour</Label>
                        <Select value={String(editing._weekday)} onValueChange={(v) => setEditing({ ...editing, _weekday: v })}>
                          <SelectTrigger className="h-10 rounded-xl border-[#DDE7E4]" data-testid="tpl-edit-weekday"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {JOURS.map((j, i) => <SelectItem key={j} value={String(i)}>{j}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {editing._schedule_type === "monthly" && (
                      <div>
                        <Label className="text-[10px] text-[#94ADA7]">Jour du mois</Label>
                        <Input type="number" min={1} max={31} value={editing._day} onChange={(e) => setEditing({ ...editing, _day: e.target.value })} className="h-10 rounded-xl border-[#DDE7E4]" data-testid="tpl-edit-day" />
                      </div>
                    )}
                    <div>
                      <Label className="text-[10px] text-[#94ADA7]">Heure</Label>
                      <Input type="number" min={0} max={23} value={editing._hour} onChange={(e) => setEditing({ ...editing, _hour: e.target.value })} className="h-10 rounded-xl border-[#DDE7E4]" data-testid="tpl-edit-hour" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-[#94ADA7]">Minute</Label>
                      <Input type="number" min={0} max={59} value={editing._minute} onChange={(e) => setEditing({ ...editing, _minute: e.target.value })} className="h-10 rounded-xl border-[#DDE7E4]" data-testid="tpl-edit-minute" />
                    </div>
                  </div>
                  <p className="text-[10px] text-[#94ADA7]">Fuseau horaire : Europe/Brussels</p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Switch checked={!!editing.enabled} onCheckedChange={(c) => setEditing({ ...editing, enabled: c })} data-testid="tpl-edit-enabled" />
                <span className="text-sm text-[#1A2B27]">Template actif</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveEdit} disabled={busy} data-testid="tpl-edit-save" className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3528]">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog aperçu */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { setPreview(null); setPreviewViewport("desktop"); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tpl-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A2B27]">Aperçu — {preview?.name}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="bg-[#F1EDE3] p-3 rounded-xl flex-1 min-w-[200px]">
                  <p className="label-tiny mb-1">Sujet</p>
                  <p className="font-semibold text-[#1A2B27]">{preview.subject}</p>
                </div>
                <div className="inline-flex rounded-full bg-[#E4EBE1] p-1 border border-[#C9DBD6]" data-testid="preview-viewport-toggle">
                  <button
                    type="button"
                    onClick={() => setPreviewViewport("desktop")}
                    data-testid="preview-viewport-desktop"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      previewViewport === "desktop"
                        ? "bg-[#2C4C3B] text-white shadow-sm"
                        : "text-[#6b7f79] hover:bg-[#C9DBD6]/60"
                    }`}
                  >
                    <Monitor className="w-3.5 h-3.5" /> Desktop
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewViewport("mobile")}
                    data-testid="preview-viewport-mobile"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      previewViewport === "mobile"
                        ? "bg-[#2C4C3B] text-white shadow-sm"
                        : "text-[#6b7f79] hover:bg-[#C9DBD6]/60"
                    }`}
                  >
                    <Smartphone className="w-3.5 h-3.5" /> Mobile
                  </button>
                </div>
              </div>
              <div
                className={
                  previewViewport === "mobile"
                    ? "flex justify-center bg-[#DDE7E4] rounded-2xl p-4"
                    : "border border-[#DDE7E4] rounded-xl overflow-hidden"
                }
              >
                <iframe
                  title="preview"
                  srcDoc={preview.html}
                  data-testid="preview-iframe"
                  style={{
                    height: previewViewport === "mobile" ? "640px" : "500px",
                    width: previewViewport === "mobile" ? "375px" : "100%",
                    maxWidth: "100%",
                    border: previewViewport === "mobile" ? "8px solid #1A2B27" : 0,
                    borderRadius: previewViewport === "mobile" ? "28px" : 0,
                    background: "#fff",
                    boxShadow: previewViewport === "mobile" ? "0 12px 32px rgba(62,39,35,0.18)" : "none",
                  }}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
